import { Button, Content, Panel, Table } from '@bbri/ui';
import { TFunction } from 'i18next';
import 'jspdf-autotable';
import { Toast } from 'primereact/toast';
import React, { ReactNode } from 'react';
import { ChevronLeft, FileText } from 'react-feather';
import ReactGA from 'react-ga';
import { withTranslation, WithTranslation } from 'react-i18next';
import { AuthService } from '../../../login/services/AuthService';
import { SeverityEnum } from '../../../shared/enum/SeverityEnum';
import { isMobileDevice } from '../../../shared/helpers/isMobileDevice';
import { ReactGAUtils } from '../../../shared/utils/ReactGAUtils';
import { Submission } from '../../../user/models/Submission';
import { SubmissionService } from '../../../user/services/SubmissionService';
import { ResponsesDTO } from '../../dto/ResponsesDTO';
import { FormType } from '../../enum/FormType';
import { QuestionType } from '../../enum/QuestionType';
import { Diagram } from '../../models/Diagram'
import { DiagramComment } from '../../models/DiagramComment';
import { Dot } from '../../models/Dot';
import { Form } from '../../models/Form';
import { FormQuestion } from '../../models/FormQuestion';
import { RequirementResult } from '../../models/RequirementResults';
import { DecisionTreeService } from '../../services/DecisionTreeService';
import { FileService } from '../../services/FileService';
import { FormService } from '../../services/FormService';
import './FormResults.scss';
import FormLoading from '../FormLoading';
import { ConfirmDialog } from './components/dialogs/FormResultConfirmDialog';
import { SubmissionEditDialog } from './components/dialogs/FormResultSubmissionEditDialog';
import { SubmissionNameDialog } from './components/dialogs/FormResultSubmissionNameDialog';
import { FormResultGraphResultView } from './components/FormResultGraphResultView';
import { FormResultTableResultView } from './components/FormResultTableResultView';
import { FormResultExportFilesService } from './services/FormResultExportFilesService';
import { FormResultGraphService } from './services/FormResultGraphService';
import { FormResultReplaceHTMLTagsService } from './services/FormResultReplaceHTMLTagsService';
/**
 * ⚠️ Must use `require` instead of `import` for Juice:
 * The currently used version (6.0.0) does not support ES Modules.
 */
const juice = require("juice")

interface Props extends WithTranslation {
  form: Form;
  history: number[];
  values: Record<string, any>;
  idSubmittedSubmission: string | null
  submission?: Submission
  results: RequirementResult[];
  onResetClick: () => void;
  onPreviousClick: () => void;
  onlyResult?: boolean
  connected?: boolean
}

interface States {
  openConfirmationModal: boolean;
  openSubmissionNameModal: boolean
  openSubmissionEditModal: boolean
  newSubmissionName: string
  htmlTable: string;
  loadingHTML: boolean;
  loadingPDF : boolean;
  loadingWord : boolean;
  allComments: DiagramComment[]
  graphInfo: Dot[] | string
  diagramName: string
  diagramInfo: Diagram | null,
  translationsReady: boolean,
}

class FormResults extends React.Component<Props, States> {
  private readonly t: TFunction;
  private toast : Toast | null
  private xlsxData: any[];
  private readonly formService: FormService;
  private readonly isOnMobile: boolean = false;
  private html2render: string = '';
  private readonly fileService: FileService;
  private readonly decisionTreeService: DecisionTreeService
  private readonly submissionService: SubmissionService
  private authService : AuthService
  private exportFilesService : FormResultExportFilesService;
  private formResultGraphService : FormResultGraphService;
  private readonly formResultReplaceHTMLTagsService : FormResultReplaceHTMLTagsService;

  constructor(props: Props) {
    super(props);
    //region INIT COMMUN

    this.t = this.props.t;
    this.toast = null
    this.xlsxData = [];
    this.isOnMobile = isMobileDevice();

    //endregion
    //region INIT SERVICES

    this.formService = new FormService();
    this.fileService = new FileService();
    this.decisionTreeService = new DecisionTreeService()
    this.submissionService = new SubmissionService()
    this.authService = new AuthService();
    this.exportFilesService =  new FormResultExportFilesService(
      this.formService,
      this.t,
      this.submissionService,
      this.fileService
    );
    this.formResultGraphService = new FormResultGraphService(this.decisionTreeService);
    this.formResultReplaceHTMLTagsService = new FormResultReplaceHTMLTagsService(
      this.formService,
      this.t
    )
    //endregion
    //region INIT STATES
    this.state = {
      openConfirmationModal: false,
      openSubmissionNameModal: false,
      openSubmissionEditModal: false,
      newSubmissionName: "",
      htmlTable: '',
      loadingHTML: true,
      loadingWord: false,
      allComments: [],
      graphInfo: [],
      diagramInfo: null,
      loadingPDF: false,
      diagramName: "",
      translationsReady: false,
    };
    //endregion
    //region TRANSLATION RELOADING
    this.props.i18n.reloadResources().then(() => {
      console.log('Translations forcefully reloaded');
      this.fileService.getOne('tableau-resultat').then((r) => {
        if (r.data.html) {
          this._replaceTagsInHTMLFile(r.data.html);
          this.setState({ translationsReady: true });
        }
        else {
          this.setState({
            loadingHTML: false,
            translationsReady: true,
          });
        }
      }, () => {
        this.setState({
          loadingHTML: false,
          translationsReady: true,
        });
      });
    });
    //endregion
  }

  componentDidMount(): void {
    if (this.props.form.type === FormType.GRAPH) {

      this.formResultGraphService._getGraphInfo(
        this.props.form.graphDecisionTree,
        this.props.values,
        (graphInfo) => this.setState({ graphInfo: graphInfo })
        )

      this.formResultGraphService._getDiagramInfo(
        this.props.form.diagramDecisionTree,
        this.props.values,
        (diagramName, diagramInfo) => this.setState({ diagramName: diagramName, diagramInfo: diagramInfo }),
        (diagramInfo) => this.setState({ diagramInfo: diagramInfo })
      )

      this.formResultGraphService._getComments(
        (allComments) => this.setState({ allComments: allComments })
      )
    }

    this._getDefaultSubmissionName()
  }

  private _getDefaultSubmissionName() {
    if (this.props.form.defaultSubmissionName) {
      this.decisionTreeService
        .computeDecisionTree(this.props.form.defaultSubmissionName, this.props.values)
          .then(
              (r) => {
                if (typeof r === "string") {
                  this.setState({
                    newSubmissionName: this.formService.t(this.t, r, false)
                  })
                }
              }
          )
          .catch(e => console.log(e))
    }
  }

  private _replaceTagsInHTMLFile(htmlFile: string) {
    this.html2render = this.formResultReplaceHTMLTagsService.replaceTags(
      htmlFile,
      this.props.form.questions,
      this.props.values,
      this.props.results
    );

    this.setState({
      loadingHTML: false,
      htmlTable: this.html2render,
      openConfirmationModal: false,
    });
  }

  private async getBase64FromUrl(url: RequestInfo) {
    const data = await fetch(url);
    const blob = await data.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      }
    });
  }

  /**
   * Create table row according to the results
   */
  createRowResults() {
    const data: ReactNode[] = [];
    this.xlsxData = [
      [
        "",
        this.formService.t(this.t, "ui_requirement_results_requirement", false, false, true),
        this.formService.t(this.t, "ui_requirement_results_result", false, false, true),
      ],
    ];

    const requirementMap = {};
    // = Sort by requirement

    for (const result of this.props.results) {

      if (this.props.form.form === 'default' && result.requirement === 'submissionProps') {
        console.log("hello")
        continue;
      }

      if (!result.answer) {
        continue;
      }

      if (!requirementMap.hasOwnProperty(result.requirement)) {
        requirementMap[result.requirement] = [];
      }
      requirementMap[result.requirement].push(result);
    }
    for (const requirement in requirementMap) {
      const options = requirementMap[requirement];
      let optionI = 0;

      for (const option of options) {
        if (!option.answer) {
          continue;
        }
        let answers = "";


        if (option.prefix) {
          answers += `${ this.formService.t(this.t, option.prefix, true, false, true) } `;
        }
        if (Array.isArray(option.answer)) {
          for (const answer of option.answer) {
            answers += this.formService.t(this.t, answer, true, false, true);
          }
        }
        else if (typeof option.answer === "object") {
          for (const answer of Object.keys(option.answer)) {
            answers += this.formService.t(this.t, answer, true, false, true);
          }
        }
        else {
          answers = this.formService.t(this.t, option.answer, true, false, true);
        }
        if (option.suffix) {
          answers += ` ${ this.formService.t(this.t, option.suffix, true, false, true) }`;
        }

        answers = answers.replace(/<p(\s*|>)/g, "<span$1").replace(/<\/p>/g, "</span>");
        // = For XLSX data export

        // Remove HTML tags
        let results = answers.replace(/<br\/?>/g, "\n");
        results = results.replace(/<\/?[A-Za-z]+>/g, "");
        this.xlsxData.push([
          requirement.toUpperCase(),
          this.formService.t(this.t, option.requirementTitle, false),
          results,
        ]);
        // = Render
        if (optionI === 0) {
          data.push(
            <tr key={ `sysD-${requirement}-${option.requirementTitle}-${optionI}` }>
              <td rowSpan={ options.length }>{ requirement.toUpperCase() }</td>
              <td rowSpan={ options.length }>
                { this.formService.t(this.t, option.requirementTitle, true) }
              </td>
              <td dangerouslySetInnerHTML={ { __html: answers } }></td>
            </tr>
          );
        }
        else {
          data.push(
            <tr key={ `sysD-${requirement}-${option.requirementTitle}-${optionI}`}>
              <td dangerouslySetInnerHTML={ { __html: answers } }></td>
            </tr>
          );
        }
        optionI++;
      }
    }

    return data;
  }


  private async loginAndSave() {
    await this.authService.loginSSO()

    this.setState({
      openSubmissionNameModal: true
    })
  }



  private _getTableResult() {
    if (this.html2render) {
      return (<div dangerouslySetInnerHTML={ { __html: this.state.htmlTable } }></div>);
    }
    else if (this.props.form.type === FormType.GRAPH) {
      return (
        <FormResultGraphResultView
          graphInfo={this.state.graphInfo}
          diagramInfo={this.state.diagramInfo}
          diagramName={this.state.diagramName}
          results={this.props.results}
          t={this.t}
          formType={this.props.form.type}
          formService={this.formService}
          allComments={this.state.allComments}
        />
      )
    }
    else if (!this.state.loadingHTML) {
      return (
        <FormResultTableResultView
          t={this.t}
          formService={this.formService}
          results={this.props.results}
          createRowResults={() => this.createRowResults()}
        />
      );
    }
  }

  private _verifyConnectedState() {
    if (this.props.connected === undefined || this.props.connected === false) {
      if (AuthService.user && AuthService.user.email !== "anonymous@bbri.be") {
        return true
      }
    }
    return this.props.connected;
  }

  render() {
    const isUserConnected = this._verifyConnectedState()
    window.scroll(0, 0);
    if (!this.state.translationsReady) {
      return (
        <FormLoading/>
      )
    }

    return (

      <div className="container containerSummary">
        <Toast ref={(el) => (this.toast = el)}/>
        <div className="table">
          <Content>
            <Content.Header>
              <div className="table-results-header">
                <h1>{ this.formService.t(this.t, "ui_requirement_results_title", true) }</h1>
                <div className="table-results-actions">
                  {(this.props.form.type === FormType.REQUIREMENT && !this.html2render && this.props.form.excelGeneration) && (
                    <Button id="exportXls" variant="action" onClick={ () => {
                      ReactGA.event({
                        category: ReactGAUtils.getCategory(),
                        action: 'click',
                        label: 'Excel'
                      });
                      this.exportFilesService.exportXLSX(this.xlsxData, this.props.form, this.props.history, this.props.values);
                    } }>
                      <i className="pi pi-file-excel"></i> XLSX
                    </Button>
                  )}
                  {(this.html2render && this.props.form.pdfFromDocx) && (
                    <Button id="exportWord" variant="action" onClick={ () => {
                      this.exportFilesService.exportPDF(
                        true,
                        this.state.htmlTable,
                        this.props.idSubmittedSubmission,
                        this.props.i18n.language,
                        (loading) => this.setState({ loadingPDF: loading }),
                        this.props.form,
                        this.props.history,
                        this.props.values,
                      );
                    } }>
                      { this.state.loadingPDF ?
                        ( <i className="pi pi-spin pi-spinner"/> )
                        :
                        ( <i className="pi pi-file-pdf"/> )
                      } PDF
                    </Button>
                  )}
                  {(this.html2render && this.props.form.pdfFromHTML) && (
                    <Button id="exportWord" variant="action" onClick={ () => {
                      this.exportFilesService.exportPDF(
                        false,
                        this.state.htmlTable,
                        this.props.idSubmittedSubmission,
                        this.props.i18n.language,
                        (loading) => this.setState({ loadingPDF: loading }),
                        this.props.form,
                        this.props.history,
                        this.props.values,
                      );
                    } }>
                      { this.state.loadingPDF ? 
                        ( <i className="pi pi-spin pi-spinner"/> )
                        :
                        ( <i className="pi pi-file-pdf"/> )
                      } PDF
                    </Button>
                  )}
                  {(this.html2render && this.props.form.docxGeneration) && (
                      <Button id="exportWord" variant="action" onClick={ () => {
                        this.exportFilesService.exportWord(
                          (loading) => this.setState({ loadingWord: loading }),
                          this.props.idSubmittedSubmission,
                          this.props.i18n.language,
                        );
                      } }>
                        { this.state.loadingWord ?
                          ( <i className="pi pi-spin pi-spinner"/> )
                          :
                          ( <i className="pi pi-file"/> )
                        } Word
                      </Button>
                  )}
                </div>
              </div>
            </Content.Header>
            <Content.Body>
              <Panel id="htmlTemplate">
                {
                  this._getTableResult()
                }
              </Panel>
              <div style={{ display: 'none' }}>
                <Table id="table-results">
                  <thead>
                  <tr>
                    <th style={ { width: "10%" } }></th>
                    <th style={ { width: "30%" } }>
                      { this.formService.t(this.t, "ui_requirement_results_requirement", true) }
                    </th>
                    <th style={ { width: "60%" } }>
                      { this.formService.t(this.t, "ui_requirement_results_result", true) }
                    </th>
                  </tr>
                  </thead>
                  <tbody>{ this.createRowResults() }</tbody>
                </Table>
              </div>
            </Content.Body>
          </Content>
        </div>
        <div className="form-actions-wrapper">
          <div className="left-actions">
            {!this.props.onlyResult && (
              <Button
                variant="secondary"
                onClick={() => this.props.onPreviousClick()}
              >
                <ChevronLeft />
                {this.formService.t(this.t, "ui_requirement_results_button_back_summary", true)}
              </Button>
            )}
          </div>
          <div className="right-actions">
            {isUserConnected && !this.props.onlyResult && (
                <>
                  <Button onClick={() => {
                    this.setState({
                      openSubmissionNameModal: true
                    })
                  }}>{this.formService.t(this.t, "ui_save_new_simulation", false)}</Button>
                  {this.props.submission && (
                      <Button onClick={() => {
                        this.setState({
                          openSubmissionEditModal: true
                        })
                      }}>{this.formService.t(this.t, "ui_modify_simulation", false)}{this.props.submission.name}</Button>
                  )}
                </>
            )}
            {isUserConnected && (
                <Button onClick={() => window.location.reload()}>{this.formService.t(this.t, "ui_go_back_to_user", false)}</Button>
            )}
            {(!isUserConnected && !this.props.onlyResult) && (
                <Button onClick={() => this.loginAndSave()}>{this.formService.t(this.t, "ui_login_save", false)}</Button>
            )}
            <Button onClick={() => {
              this.setState({ openConfirmationModal: !this.state.openConfirmationModal });
            }}>
              {this.formService.t(this.t, "ui_requirement_results_button_new_form", true)}
              <FileText/>
            </Button>
          </div>
        </div>
        <div className="confirm-dialog">
          <ConfirmDialog
            t={this.t}
            formService={this.formService}
            isVisible={this.state.openConfirmationModal}
            onCancel={() => this.setState({ openConfirmationModal: false })}
            onConfirm={() => {
              ReactGA.event({
                category: ReactGAUtils.getCategory(),
                action: 'click',
                label: 'Nouveau formulaire'
              });
              this.props.onResetClick();
            }}
            isOnMobile={this.isOnMobile}
          />

          <SubmissionNameDialog
            t={this.t}
            formService={this.formService}
            isVisible={this.state.openSubmissionNameModal}
            name={this.state.newSubmissionName}
            onChange={(value: string) => this.setState({ newSubmissionName: value })}
            onSave={() => {
              if (AuthService.user?.sub && this.props.idSubmittedSubmission) {
                this.submissionService.saveLastSubmission(this.props.idSubmittedSubmission, this.state.newSubmissionName)
                  .then(() => {
                    window.location.href = "user"
                  })
                  .catch(() => {
                    this.toast?.show({
                      severity: SeverityEnum.ERROR,
                      detail: this.formService.t(this.props.t, "ui_duplicate_name_error", false, false),
                    })
                  })
              }
            }}
            onClose={() => this.setState({ openSubmissionNameModal: false })}
            isOnMobile={this.isOnMobile}
          />

          <SubmissionEditDialog
            isVisible={this.state.openSubmissionEditModal}
            onClose={() => this.setState({ openSubmissionEditModal: false })}
            onConfirm={() => {
              const responseDTO = new ResponsesDTO(this.props.values).responses;
              this.formService.updateSubmission("default", {
                id: this.props.submission?._id,
                responses: responseDTO.filter(r => JSON.stringify(r.value) !== '{}'),
                history: this.props.history,
                name: this.props.submission?.name
              }).then(() => {
                window.location.href = "user";
              });
            }}
            formService={this.formService}
            t={this.t}
            isOnMobile={this.isOnMobile}
            submission={this.props.submission}
            values={this.props.values}
            history={this.props.history}
          />
        </div>
      </div>
    );
  }

}

export default withTranslation()(FormResults);
